/**
 * CadApi — the stable programmatic surface over the parametric document.
 *
 * Everything the GUI can do, expressed as plain async functions with
 * JSON-friendly inputs/outputs. This is the ONE layer the LLM assistant's
 * built-in tools call, the sandbox `api` object custom tools receive, and
 * (being doc.apply underneath) every call lands in undo history exactly
 * like a GUI action.
 */

import type { CadDocument } from '@core/document';
import { evalParamTable } from '@core/expr';
import { MATERIALS, massOf, materialById, type MaterialDef } from '@core/materials';
import { nearestFaceTriangle } from '@core/fea/hexSolver';
import { runFeaSolve } from '@workers/feaClient';
import { buildSketchShapes } from '@core/geometry/sketch';
import {
  countClosedLoops,
  gearEntities,
  polygonEntities,
  roundedRectEntities,
  slotEntities,
  starEntities,
  transformEntities,
} from '@core/geometry/sketchOps';
import type { GeometryResult } from '@core/geometry/types';
import { expandTextEntities } from '@core/text/fontCache';
import {
  ref,
  type FeatureId,
  type SketchEntity,
  type SketchPlane,
  type Vec2,
  type Vec3,
} from '@core/timeline/types';

export interface UiHooks {
  select?(id: FeatureId | null): void;
  fitView?(): void;
  /**
   * Render + capture the viewport as a compact JPEG data URL (vision).
   * `view` picks a standard direction (iso/front/back/left/right/top/bottom)
   * without disturbing the user's camera; omitted = current view.
   */
  capture?(view?: string): Promise<string>;
  /** Create a new project, switch to it, and return its id. */
  createProject?(name?: string): Promise<string>;
  /** Switch the live document to an existing project by id. */
  switchProject?(id: string): Promise<void>;
  /** Delete a project by id (no confirm); auto-switches if it was live. */
  deleteProject?(id: string): Promise<void>;
  /** Paint FEA von Mises vertex colors on a body + show the legend. */
  showStress?(
    id: FeatureId,
    vertexVonMises: Float32Array,
    info: {
      maxVonMises: number;
      factorOfSafety: number;
      material: string;
      /** Per-vertex xyz displacement (mm) for the deformed view. */
      displacement: Float32Array;
      maxDisplacement: number;
      /** Exaggeration factor; 0 = auto (≈8% of the body's diagonal). */
      deformationScale: number;
    },
  ): void;
  /** Remove the FEA stress overlay/legend. */
  clearStress?(): void;
}

export interface FeatureSummary {
  id: string;
  name: string;
  type: string;
  suppressed: boolean;
  status: string;
  params: unknown;
}

export interface BodyInfo {
  bbox: { min: Vec3; max: Vec3 };
  volume: number;
  triangles: number;
  consumed: boolean;
}

const deg = (d: number): number => (d * Math.PI) / 180;

export class CadApi {
  constructor(
    private readonly getDoc: () => CadDocument,
    private readonly ui: UiHooks = {},
  ) {}

  private get doc(): CadDocument {
    return this.getDoc();
  }

  // --- inspection --------------------------------------------------------------

  listFeatures(): FeatureSummary[] {
    const report = this.doc.lastReport;
    return this.doc.timeline.features.map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      suppressed: f.suppressed,
      status: report?.statuses.get(f.id)?.state ?? 'unknown',
      params: f.params,
    }));
  }

  bodyInfo(id: string): BodyInfo | null {
    const report = this.doc.lastReport;
    const result = report?.results.get(id as FeatureId);
    if (!report || result?.kind !== 'body' || result.mesh.positions.length === 0) return null;
    const p = result.mesh.positions;
    const min: [number, number, number] = [Infinity, Infinity, Infinity];
    const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < p.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        const v = p[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
    let vol = 0;
    const I = result.mesh.indices;
    for (let t = 0; t < I.length; t += 3) {
      const a = I[t]! * 3, b = I[t + 1]! * 3, c = I[t + 2]! * 3;
      vol +=
        p[a]! * (p[b + 1]! * p[c + 2]! - p[b + 2]! * p[c + 1]!) +
        p[a + 1]! * (p[b + 2]! * p[c]! - p[b]! * p[c + 2]!) +
        p[a + 2]! * (p[b]! * p[c + 1]! - p[b + 1]! * p[c]!);
    }
    return {
      bbox: { min, max },
      volume: vol / 6,
      triangles: I.length / 3,
      consumed: report.consumed.has(id as FeatureId),
    };
  }

  /** The evaluation status/error of one feature. */
  featureStatus(id: string): { state: string; message?: string } {
    const s = this.doc.lastReport?.statuses.get(id as FeatureId);
    if (!s) return { state: 'unknown' };
    return { state: s.state, ...(s.state === 'error' ? { message: s.message } : {}) };
  }

  // --- creation ----------------------------------------------------------------

  private async add<T extends Parameters<CadDocument['apply']>[1]>(
    label: string,
    mutate: T,
  ): Promise<{ id: string; name: string; status: string }> {
    // Capture the live document ONCE: a concurrent switch_project (or a user
    // clicking another project) can swap this.doc across the await, and the
    // post-apply diff / select must run against the same doc we mutated.
    const doc = this.doc;
    const before = new Set(doc.timeline.features.map((f) => f.id));
    await doc.apply(label, mutate);
    const created = doc.timeline.features.find((f) => !before.has(f.id));
    if (!created) throw new Error('No feature was created');
    const s = doc.lastReport?.statuses.get(created.id);
    const state = s?.state ?? 'unknown';
    if (s?.state === 'error') {
      throw new Error(`Created ${created.name} but it failed: ${s.message}`);
    }
    if (doc === this.doc) this.ui.select?.(created.id); // only if still live
    return { id: created.id, name: created.name, status: state };
  }

  /**
   * Macro entities the LLM may pass alongside real ones: polygon and slot
   * expand to lines/arcs here so callers never hand-compute vertices.
   */
  private expandMacros(entities: unknown[]): SketchEntity[] {
    const out: SketchEntity[] = [];
    for (const raw of entities) {
      const e = raw as Record<string, unknown> & { kind: string };
      if (e.kind === 'polygon') {
        const c = e.center as Vec2 | undefined;
        const r = e.radius as number;
        if (!c || !(r > 0)) throw new Error('polygon needs {center:[x,y], radius>0, sides?}');
        out.push(...polygonEntities(c, r, (e.sides as number) ?? 6));
      } else if (e.kind === 'slot') {
        const slot =
          e.a && e.b && (e.width as number) > 0
            ? slotEntities(e.a as Vec2, e.b as Vec2, e.width as number)
            : null;
        if (!slot) throw new Error('slot needs {a:[x,y], b:[x,y], width>0} with a ≠ b');
        out.push(...slot);
      } else if (e.kind === 'rounded_rect') {
        const rr =
          e.min && e.max
            ? roundedRectEntities(e.min as Vec2, e.max as Vec2, (e.cornerRadius as number) ?? 0)
            : null;
        if (!rr) {
          throw new Error(
            'rounded_rect needs {min:[x,y], max:[x,y], cornerRadius>0} with 2·cornerRadius < the smaller side',
          );
        }
        out.push(...rr);
      } else if (e.kind === 'star') {
        const star =
          e.center
            ? starEntities(e.center as Vec2, e.outerRadius as number, e.innerRadius as number, (e.points as number) ?? 5)
            : null;
        if (!star) throw new Error('star needs {center:[x,y], outerRadius, innerRadius < outerRadius, points?}');
        out.push(...star);
      } else if (e.kind === 'gear') {
        const gear =
          e.center
            ? gearEntities(
                e.center as Vec2,
                e.module as number,
                e.teeth as number,
                (e.pressureAngleDeg as number) ?? 20,
                (e.rotateDeg as number) ?? 0,
              )
            : null;
        if (!gear) throw new Error('gear needs {center:[x,y], module>0, teeth:6..200, pressureAngleDeg?, boreDiameter?}');
        out.push(...gear);
        const bore = e.boreDiameter as number | undefined;
        if (bore && bore > 0) out.push({ kind: 'circle', center: e.center as Vec2, radius: bore / 2 });
      } else {
        out.push(raw as SketchEntity);
      }
    }
    return out;
  }

  /**
   * Normalize a plane spec. Accepts the raw SketchPlane kinds AND the sugar
   * {kind:"on_face", bodyId, faceIndex?} / {kind:"on_face", bodyId,
   * near:[x,y,z]} — resolved to a face-attached (bface) plane from the exact
   * B-rep so the sketch rides that planar face through edits.
   */
  /** All PLANAR faces of a body (uncapped, unlike faceInfo's 64-face list). */
  private planarFacesOf(bodyId: string): { index: number; centroid: Vec3; normal: Vec3 }[] {
    const result = this.doc.lastReport?.results.get(bodyId as FeatureId);
    if (result?.kind !== 'body' || !result.mesh.faceKinds || !result.mesh.faceData) {
      throw new Error(`${bodyId} has no B-rep face data (mesh-only bodies have none)`);
    }
    const kinds = result.mesh.faceKinds;
    const data = result.mesh.faceData;
    const r = (i: number): number => Math.round(data[i]! * 1000) / 1000;
    const out: { index: number; centroid: Vec3; normal: Vec3 }[] = [];
    for (let f = 0; f < kinds.length; f++) {
      if (kinds[f] !== 1) continue;
      const o = f * 10;
      out.push({ index: f, centroid: [r(o), r(o + 1), r(o + 2)], normal: [r(o + 3), r(o + 4), r(o + 5)] });
    }
    return out;
  }

  private resolveSketchPlane(plane: unknown): SketchPlane {
    const p = plane as Record<string, unknown> & { kind?: string };
    if (!p || p.kind !== 'on_face') return (plane as SketchPlane) ?? { kind: 'datum', plane: 'XY', offset: 0 };
    const bodyId = p.bodyId as string;
    const planar = this.planarFacesOf(bodyId); // uncapped — a face index >= 64 resolves fine
    if (planar.length === 0) throw new Error(`${bodyId} has no planar faces to sketch on`);
    let chosen = planar[0]!;
    if (typeof p.faceIndex === 'number') {
      const byIndex = planar.find((f) => f.index === p.faceIndex);
      if (!byIndex) throw new Error(`face ${p.faceIndex} of ${bodyId} is not a planar face (see face_info)`);
      chosen = byIndex;
    } else if (p.near !== undefined) {
      const near = p.near;
      if (
        !Array.isArray(near) || near.length !== 3 ||
        near.some((v) => typeof v !== 'number' || !Number.isFinite(v))
      ) {
        throw new Error('on_face "near" must be [x,y,z] — three finite numbers');
      }
      const [nx, ny, nz] = near as unknown as Vec3;
      let best = Infinity;
      for (const f of planar) {
        const d = Math.hypot(f.centroid[0] - nx, f.centroid[1] - ny, f.centroid[2] - nz);
        if (d < best) { best = d; chosen = f; }
      }
    }
    return { kind: 'bface', body: ref(bodyId as FeatureId), center: chosen.centroid, normal: chosen.normal };
  }

  async createSketch(opts: {
    name?: string;
    plane?: SketchPlane;
    entities: SketchEntity[];
  }): Promise<{ id: string; name: string; status: string }> {
    const plane = this.resolveSketchPlane(opts.plane);
    const n = this.doc.timeline.features.filter((f) => f.type === 'sketch').length + 1;
    const name = opts.name ?? `Sketch ${n}`;
    const entities = this.expandMacros(opts.entities);
    return this.add(`Create ${name}`, (t) =>
      t.addFeature('sketch', name, { plane, entities }).timeline,
    );
  }

  /** Append entities (macros welcome) to an existing sketch. */
  async addSketchEntities(opts: {
    sketchId: string;
    entities: SketchEntity[];
  }): Promise<{ status: string; entityCount: number }> {
    const feature = this.doc.timeline.get(opts.sketchId as FeatureId);
    if (!feature || feature.type !== 'sketch') throw new Error(`${opts.sketchId} is not a sketch`);
    const current = (feature.params as unknown as { entities: SketchEntity[] }).entities;
    const entities = [...current, ...this.expandMacros(opts.entities)];
    const r = await this.updateFeature(opts.sketchId, { entities });
    return { status: r.status, entityCount: entities.length };
  }

  /**
   * In-sketch pattern: clone entities around a center (circular — bolt
   * circles, tooth features) or along a step (linear). Original stays;
   * count includes it. Omit `indices` to pattern the whole sketch.
   */
  async patternSketchEntities(opts: {
    sketchId: string;
    kind: 'circular' | 'linear';
    count: number;
    indices?: number[];
    center?: Vec2;
    totalAngleDeg?: number;
    step?: Vec2;
  }): Promise<{ status: string; entityCount: number }> {
    const feature = this.doc.timeline.get(opts.sketchId as FeatureId);
    if (!feature || feature.type !== 'sketch') throw new Error(`${opts.sketchId} is not a sketch`);
    const count = Math.round(opts.count);
    if (!(count >= 2) || count > 100) throw new Error('pattern needs count 2..100');
    const current = (feature.params as unknown as { entities: SketchEntity[] }).entities;
    const source = opts.indices
      ? opts.indices.map((i) => {
          const e = current[i];
          if (!e) throw new Error(`no entity at index ${i} (sketch has ${current.length})`);
          return e;
        })
      : current;
    const clones: SketchEntity[] = [];
    for (let k = 1; k < count; k++) {
      let xformed: SketchEntity[] | null;
      if (opts.kind === 'circular') {
        if (!opts.center) throw new Error('circular pattern needs {center:[x,y]}');
        const total = ((opts.totalAngleDeg ?? 360) * Math.PI) / 180;
        const full = Math.abs((opts.totalAngleDeg ?? 360) - 360) < 1e-9;
        const step = full ? total / count : total / (count - 1);
        xformed = transformEntities(source, { angle: step * k, about: opts.center });
      } else {
        if (!opts.step) throw new Error('linear pattern needs {step:[dx,dy]}');
        xformed = transformEntities(source, {
          translate: [opts.step[0] * k, opts.step[1] * k],
        });
      }
      if (!xformed) {
        // Name the actual offender — the LLM acts on this message literally.
        const hasEllipse = source.some((e) => e.kind === 'ellipse');
        throw new Error(
          hasEllipse && opts.kind === 'circular'
            ? 'ellipses cannot be rotated (axis-aligned only) — exclude them via indices, use a spline, or pattern the extruded body instead'
            : 'text entities cannot be patterned — pattern the extruded body instead',
        );
      }
      clones.push(...xformed);
    }
    const entities = [...current, ...clones];
    const r = await this.updateFeature(opts.sketchId, { entities });
    return { status: r.status, entityCount: entities.length };
  }

  /**
   * Diagnose a sketch BEFORE using it: which closed profiles exist (index,
   * area, bbox, hole count — profile:0 is the largest) and which chains are
   * still open (with endpoints, so a gap can be closed). 2D sketch coords.
   */
  sketchInfo(sketchId: string): {
    profiles: { index: number; area: number; holes: number; bbox: { min: Vec2; max: Vec2 } }[];
    openChains: { start: Vec2; end: Vec2; gap: number; points: number }[];
    entityCount: number;
  } {
    const result = this.doc.lastReport?.results.get(sketchId as FeatureId);
    if (result?.kind !== 'sketch') throw new Error(`${sketchId} is not an evaluated sketch`);
    const entities = expandTextEntities(result.entities);
    const { profiles, paths } = buildSketchShapes(entities, result.planeWorld);
    const area2d = (loop: readonly Vec2[]): number => {
      let a = 0;
      for (let i = 0; i < loop.length; i++) {
        const p = loop[i]!;
        const q = loop[(i + 1) % loop.length]!;
        a += p[0] * q[1] - q[0] * p[1];
      }
      return Math.abs(a / 2);
    };
    return {
      profiles: profiles.map((p, index) => {
        const min: [number, number] = [Infinity, Infinity];
        const max: [number, number] = [-Infinity, -Infinity];
        for (const [x, y] of p.outer) {
          min[0] = Math.min(min[0], x); min[1] = Math.min(min[1], y);
          max[0] = Math.max(max[0], x); max[1] = Math.max(max[1], y);
        }
        return { index, area: area2d(p.outer), holes: p.holes.length, bbox: { min, max } };
      }),
      openChains: paths.map((chain) => {
        const start = chain[0]!;
        const end = chain[chain.length - 1]!;
        return {
          start,
          end,
          gap: Math.hypot(end[0] - start[0], end[1] - start[1]),
          points: chain.length,
        };
      }),
      entityCount: result.entities.length,
    };
  }

  /**
   * The identity data of a body's faces, from the exact B-rep: planar faces
   * (centroid + outward normal — sketch/hole targets) and cylindrical faces
   * (axis + radius — concentric-mate targets). World mm.
   */
  faceInfo(bodyId: string): {
    faces: (
      | { index: number; kind: 'planar'; centroid: Vec3; normal: Vec3 }
      | { index: number; kind: 'cylindrical'; centroid: Vec3; axis: Vec3; axisPoint: Vec3; radius: number }
      | { index: number; kind: 'other'; centroid: Vec3 }
    )[];
    truncated: boolean;
  } {
    const result = this.doc.lastReport?.results.get(bodyId as FeatureId);
    if (result?.kind !== 'body' || !result.mesh.faceKinds || !result.mesh.faceData) {
      throw new Error(`${bodyId} has no B-rep face data (mesh-only bodies have none)`);
    }
    const kinds = result.mesh.faceKinds;
    const data = result.mesh.faceData;
    const MAX = 64;
    const rounded = (i: number): number => Math.round(data[i]! * 1000) / 1000;
    const v3 = (i: number): Vec3 => [rounded(i), rounded(i + 1), rounded(i + 2)];
    const faces = [];
    for (let f = 0; f < kinds.length && faces.length < MAX; f++) {
      const o = f * 10;
      const centroid = v3(o);
      if (kinds[f] === 1) faces.push({ index: f, kind: 'planar' as const, centroid, normal: v3(o + 3) });
      else if (kinds[f] === 2) {
        faces.push({
          index: f, kind: 'cylindrical' as const, centroid,
          axis: v3(o + 3), axisPoint: v3(o + 6), radius: rounded(o + 9),
        });
      } else faces.push({ index: f, kind: 'other' as const, centroid });
    }
    return { faces, truncated: kinds.length > MAX };
  }

  /**
   * Midpoints of a body's B-rep edges — the handles fillet/chamfer take.
   * Pass a subset of these midpoints as `edges` to round only those.
   */
  edgeInfo(bodyId: string): { edges: { index: number; midpoint: Vec3 }[]; truncated: boolean } {
    const result = this.doc.lastReport?.results.get(bodyId as FeatureId);
    if (result?.kind !== 'body' || !result.mesh.edgeMidpoints) {
      throw new Error(`${bodyId} has no B-rep edge data (mesh-only bodies have none)`);
    }
    const m = result.mesh.edgeMidpoints;
    const MAX = 128;
    const edges = [];
    for (let i = 0; i * 3 + 2 < m.length && i < MAX; i++) {
      edges.push({
        index: i,
        midpoint: [
          Math.round(m[i * 3]! * 1000) / 1000,
          Math.round(m[i * 3 + 1]! * 1000) / 1000,
          Math.round(m[i * 3 + 2]! * 1000) / 1000,
        ] as Vec3,
      });
    }
    return { edges, truncated: m.length / 3 > MAX };
  }

  /** Fillet (round) edges. Omit `edges` for ALL; else pass edge midpoints. */
  async fillet(opts: {
    bodyId: string;
    radius: number;
    edges?: Vec3[];
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    if (!(opts.radius > 0)) throw new Error('fillet needs radius > 0');
    const n = this.doc.timeline.features.filter((f) => f.type === 'fillet').length + 1;
    const name = opts.name ?? `Fillet ${n}`;
    return this.add(name, (t) =>
      t.addFeature('fillet', name, {
        target: ref(opts.bodyId as FeatureId),
        radius: opts.radius,
        ...(opts.edges?.length ? { edges: opts.edges } : {}),
      }).timeline,
    );
  }

  /** Chamfer (bevel) edges. Omit `edges` for ALL; else pass edge midpoints. */
  async chamfer(opts: {
    bodyId: string;
    distance: number;
    edges?: Vec3[];
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    if (!(opts.distance > 0)) throw new Error('chamfer needs distance > 0');
    const n = this.doc.timeline.features.filter((f) => f.type === 'chamfer').length + 1;
    const name = opts.name ?? `Chamfer ${n}`;
    return this.add(name, (t) =>
      t.addFeature('chamfer', name, {
        target: ref(opts.bodyId as FeatureId),
        distance: opts.distance,
        ...(opts.edges?.length ? { edges: opts.edges } : {}),
      }).timeline,
    );
  }

  /** Hollow a body into walls of `thickness`, optionally opening one side. */
  async shell(opts: {
    bodyId: string;
    thickness: number;
    open?: 'top' | 'bottom' | 'none';
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    if (!(opts.thickness > 0)) throw new Error('shell needs thickness > 0');
    const n = this.doc.timeline.features.filter((f) => f.type === 'shell').length + 1;
    const name = opts.name ?? `Shell ${n}`;
    return this.add(name, (t) =>
      t.addFeature('shell', name, {
        target: ref(opts.bodyId as FeatureId),
        thickness: opts.thickness,
        open: opts.open ?? 'top',
      }).timeline,
    );
  }

  async extrude(opts: {
    sketchId: string;
    distance: number;
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    endScale?: number;
    profileIndex?: number;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'extrude').length + 1;
    const name = opts.name ?? `Extrude ${n}`;
    return this.add(name, (t) =>
      t.addFeature('extrude', name, {
        // Explicit index picks one region; omitted = ALL closed regions.
        profile:
          opts.profileIndex !== undefined
            ? ref(opts.sketchId as FeatureId, `profile:${opts.profileIndex}`)
            : ref(opts.sketchId as FeatureId),
        distance: opts.distance,
        op: opts.op ?? 'new-body',
        ...(opts.endScale !== undefined ? { endScale: opts.endScale } : {}),
      }).timeline,
    );
  }

  async revolve(opts: {
    sketchId: string;
    angleDeg?: number;
    axisOrigin?: [number, number];
    axisDirection?: [number, number];
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    profileIndex?: number;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'revolve').length + 1;
    const name = opts.name ?? `Revolve ${n}`;
    return this.add(name, (t) =>
      t.addFeature('revolve', name, {
        profile: ref(opts.sketchId as FeatureId, `profile:${opts.profileIndex ?? 0}`),
        axisOrigin: opts.axisOrigin ?? [0, 0],
        axisDirection: opts.axisDirection ?? [0, 1],
        angle: deg(opts.angleDeg ?? 360),
        op: opts.op ?? 'new-body',
      }).timeline,
    );
  }

  async loft(opts: {
    sketchIds: string[];
    ruled?: boolean;
    spineSketchId?: string;
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    if (!Array.isArray(opts.sketchIds) || opts.sketchIds.length < 2) {
      throw new Error('loft needs sketchIds: 2+ closed-profile sketches in order');
    }
    const n = this.doc.timeline.features.filter((f) => f.type === 'loft').length + 1;
    const name = opts.name ?? `Loft ${n}`;
    return this.add(name, (t) =>
      t.addFeature('loft', name, {
        profiles: opts.sketchIds.map((id) => ref(id as FeatureId, 'profile:0')),
        ...(opts.ruled ? { ruled: true } : {}),
        ...(opts.spineSketchId ? { spine: ref(opts.spineSketchId as FeatureId) } : {}),
        op: opts.op ?? 'new-body',
      }).timeline,
    );
  }

  async sweep(opts: {
    profileSketchId: string;
    pathSketchId: string;
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'sweep').length + 1;
    const name = opts.name ?? `Sweep ${n}`;
    return this.add(name, (t) =>
      t.addFeature('sweep', name, {
        profile: ref(opts.profileSketchId as FeatureId, 'profile:0'),
        path: ref(opts.pathSketchId as FeatureId),
        op: opts.op ?? 'new-body',
      }).timeline,
    );
  }

  private centerOf(bodyId: string): Vec3 {
    const info = this.bodyInfo(bodyId);
    if (!info) throw new Error(`Feature ${bodyId} has no body geometry`);
    return [
      (info.bbox.min[0] + info.bbox.max[0]) / 2,
      (info.bbox.min[1] + info.bbox.max[1]) / 2,
      (info.bbox.min[2] + info.bbox.max[2]) / 2,
    ];
  }

  async scaleBody(opts: {
    bodyId: string;
    factors?: [number, number, number];
    factor?: number;
    center?: Vec3;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const f = opts.factor ?? 1;
    const factors = opts.factors ?? ([f, f, f] as [number, number, number]);
    const center = opts.center ?? this.centerOf(opts.bodyId);
    const n = this.doc.timeline.features.filter((x) => x.type === 'scale').length + 1;
    const name = opts.name ?? `Scale ${n}`;
    return this.add(name, (t) =>
      t.addFeature('scale', name, { target: ref(opts.bodyId as FeatureId), factors, center })
        .timeline,
    );
  }

  async mirrorBody(opts: {
    bodyId: string;
    plane: 'YZ' | 'XZ' | 'XY';
    offset?: number;
    keepOriginal?: boolean;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((x) => x.type === 'mirror').length + 1;
    const name = opts.name ?? `Mirror ${n}`;
    return this.add(name, (t) =>
      t.addFeature('mirror', name, {
        target: ref(opts.bodyId as FeatureId),
        plane: opts.plane,
        offset: opts.offset ?? 0,
        keepOriginal: opts.keepOriginal ?? true,
      }).timeline,
    );
  }

  async moveBody(opts: {
    bodyId: string;
    translation?: Vec3;
    rotateAxis?: 'X' | 'Y' | 'Z';
    angleDeg?: number;
    copies?: number;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const center = this.centerOf(opts.bodyId);
    const n = this.doc.timeline.features.filter((x) => x.type === 'transform').length + 1;
    const name = opts.name ?? `Move ${n}`;
    return this.add(name, (t) =>
      t.addFeature('transform', name, {
        target: ref(opts.bodyId as FeatureId),
        translation: opts.translation ?? [0, 0, 0],
        rotationAxis: opts.rotateAxis ?? 'Z',
        rotationAngle: deg(opts.angleDeg ?? 0),
        rotationCenter: center,
        copies: opts.copies ?? 0,
      }).timeline,
    );
  }

  async groupBodies(opts: { bodyIds: string[]; name?: string }): Promise<{
    id: string;
    name: string;
    status: string;
  }> {
    const n = this.doc.timeline.features.filter((x) => x.type === 'group').length + 1;
    const name = opts.name ?? `Group ${n}`;
    const ids = [...opts.bodyIds].sort(
      (a, b) =>
        this.doc.timeline.positionOf(a as FeatureId) -
        this.doc.timeline.positionOf(b as FeatureId),
    );
    return this.add(name, (t) =>
      t.addFeature('group', name, { targets: ids.map((id) => ref(id as FeatureId)) }).timeline,
    );
  }

  async instanceBody(opts: {
    sourceId: string;
    translation?: Vec3;
    rotateAxis?: 'X' | 'Y' | 'Z';
    angleDeg?: number;
    copies?: number;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const center = this.centerOf(opts.sourceId);
    const n = this.doc.timeline.features.filter((x) => x.type === 'instance').length + 1;
    const name = opts.name ?? `Instance ${n}`;
    return this.add(name, (t) =>
      t.addFeature('instance', name, {
        source: ref(opts.sourceId as FeatureId),
        translation: opts.translation ?? [0, 0, 0],
        rotationAxis: opts.rotateAxis ?? 'Z',
        rotationAngle: deg(opts.angleDeg ?? 0),
        rotationCenter: center,
        copies: opts.copies ?? 0,
      }).timeline,
    );
  }

  // --- parameters -------------------------------------------------------------------

  /** Named document parameters with their evaluated values (or errors). */
  listParameters(): { name: string; expr: string; value?: number; error?: string }[] {
    const table = this.doc.timeline.params;
    const { values, errors } = evalParamTable(table);
    return Object.entries(table).map(([name, expr]) => ({
      name,
      expr,
      ...(name in errors ? { error: errors[name]! } : { value: values[name]! }),
    }));
  }

  /** Create/update a named parameter. expr may reference other parameters. */
  async setParameter(name: string, expr: string): Promise<{ value?: number; error?: string }> {
    await this.doc.apply(`Set parameter ${name} = ${expr}`, (t) => t.setParam(name, String(expr)));
    const { values, errors } = evalParamTable(this.doc.timeline.params);
    return name in errors ? { error: errors[name]! } : { value: values[name]! };
  }

  async deleteParameter(name: string): Promise<void> {
    await this.doc.apply(`Delete parameter ${name}`, (t) => t.deleteParam(name));
  }

  // --- assemblies -------------------------------------------------------------------

  /** Insert another project as a linked assembly component. */
  async insertPart(opts: {
    projectId: string;
    position?: Vec3;
    rotationEulerDeg?: Vec3;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    if (opts.projectId === this.doc.id) throw new Error('A project cannot contain itself');
    const { db } = await import('@core/storage/db');
    const rec = await db.projects.get(opts.projectId);
    if (!rec) throw new Error(`No project with id ${opts.projectId} — see list_projects`);
    const n = this.doc.timeline.features.filter((f) => f.type === 'part').length + 1;
    const name = opts.name ?? `${rec.name} ${n}`;
    const r = (opts.rotationEulerDeg ?? [0, 0, 0]).map(deg) as unknown as Vec3;
    return this.add(`Insert ${rec.name}`, (t) =>
      t.addFeature('part', name, {
        projectId: opts.projectId,
        source: rec.name,
        position: opts.position ?? [0, 0, 0],
        rotationEuler: r,
      }).timeline,
    );
  }

  /** Drill holes into a body at face points, along the given normals. */
  async hole(opts: {
    bodyId: string;
    holes: { point: Vec3; normal?: Vec3 }[];
    diameter?: number;
    depth?: number | 'through';
    style?: 'plain' | 'counterbore' | 'countersink';
    cbDiameter?: number;
    cbDepth?: number;
    csDiameter?: number;
    csAngle?: number;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    if (!opts.holes?.length) throw new Error('hole needs at least one {point} position');
    const n = this.doc.timeline.features.filter((f) => f.type === 'hole').length + 1;
    const name = opts.name ?? `Hole ${n}`;
    return this.add(name, (t) =>
      t.addFeature('hole', name, {
        target: ref(opts.bodyId as FeatureId),
        holes: opts.holes.map((h) => ({ point: h.point, normal: h.normal ?? [0, 0, 1] })),
        diameter: opts.diameter ?? 5.5,
        depth: opts.depth ?? 'through',
        style: opts.style ?? 'plain',
        ...(opts.cbDiameter !== undefined ? { cbDiameter: opts.cbDiameter } : {}),
        ...(opts.cbDepth !== undefined ? { cbDepth: opts.cbDepth } : {}),
        ...(opts.csDiameter !== undefined ? { csDiameter: opts.csDiameter } : {}),
        ...(opts.csAngle !== undefined ? { csAngle: opts.csAngle } : {}),
      }).timeline,
    );
  }

  /** Pattern a body linearly or around an axis (count may be an expression). */
  async pattern(opts: {
    bodyId: string;
    kind: 'linear' | 'circular';
    count: number | { $expr: string };
    step?: Vec3;
    axis?: 'X' | 'Y' | 'Z';
    center?: Vec3;
    totalAngleDeg?: number;
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'pattern').length + 1;
    const name = opts.name ?? `Pattern ${n}`;
    return this.add(name, (t) =>
      t.addFeature('pattern', name, {
        target: ref(opts.bodyId as FeatureId),
        kind: opts.kind,
        count: opts.count as never,
        ...(opts.step ? { step: opts.step } : {}),
        ...(opts.axis ? { axis: opts.axis } : {}),
        ...(opts.center ? { center: opts.center } : {}),
        ...(opts.totalAngleDeg !== undefined ? { totalAngleDeg: opts.totalAngleDeg } : {}),
      }).timeline,
    );
  }

  /** Analytic primitive solid — no sketch needed. See PrimitiveParams. */
  async primitive(opts: {
    shape: 'box' | 'cylinder' | 'sphere' | 'cone' | 'torus';
    at?: Vec3;
    axis?: 'X' | 'Y' | 'Z';
    dx?: number | { $expr: string };
    dy?: number | { $expr: string };
    dz?: number | { $expr: string };
    radius?: number | { $expr: string };
    radius2?: number | { $expr: string };
    height?: number | { $expr: string };
    majorRadius?: number | { $expr: string };
    minorRadius?: number | { $expr: string };
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'primitive').length + 1;
    const name = opts.name ?? `${opts.shape[0]!.toUpperCase()}${opts.shape.slice(1)} ${n}`;
    const SIZE_KEYS = ['dx', 'dy', 'dz', 'radius', 'radius2', 'height', 'majorRadius', 'minorRadius'] as const;
    return this.add(name, (t) =>
      t.addFeature('primitive', name, {
        shape: opts.shape,
        at: opts.at ?? [0, 0, 0],
        axis: opts.axis ?? 'Z',
        ...Object.fromEntries(SIZE_KEYS.filter((k) => opts[k] !== undefined).map((k) => [k, opts[k]])),
        op: opts.op ?? 'new-body',
      } as never).timeline,
    );
  }

  /**
   * STEP text of the current bodies (default: all visible exact solids),
   * XCAF-named after their features. Large — meant for MCP clients that
   * save it to a file, not for reading inline.
   */
  async exportStep(opts?: { bodyIds?: string[] }): Promise<{ step: string; bytes: number }> {
    const step = await this.doc.exportStep(opts?.bodyIds as FeatureId[] | undefined);
    return { step, bytes: step.length };
  }

  /** Direct-model press/pull: prism the planar face nearest `facePoint`. */
  async pressPull(opts: {
    bodyId: string;
    facePoint: Vec3;
    offset: number | { $expr: string };
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'press-pull').length + 1;
    const name = opts.name ?? `Press/Pull ${n}`;
    return this.add(name, (t) =>
      t.addFeature('press-pull', name, {
        target: ref(opts.bodyId as FeatureId),
        facePoint: opts.facePoint,
        offset: opts.offset as never,
      }).timeline,
    );
  }

  /** Mold draft: tilt walls about a neutral plane along the pull axis. */
  async draft(opts: {
    bodyId: string;
    angleDeg: number | { $expr: string };
    axis?: 'X' | 'Y' | 'Z';
    neutralOffset?: number | { $expr: string };
    faces?: Vec3[];
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'draft').length + 1;
    const name = opts.name ?? `Draft ${n}`;
    return this.add(name, (t) =>
      t.addFeature('draft', name, {
        target: ref(opts.bodyId as FeatureId),
        angleDeg: opts.angleDeg as never,
        axis: opts.axis ?? 'Z',
        neutralOffset: (opts.neutralOffset as never) ?? 0,
        ...(opts.faces?.length ? { faces: opts.faces } : {}),
      }).timeline,
    );
  }

  /** ISO coarse pitches (mm) by nominal diameter — M2…M20. */
  static readonly ISO_PITCH: Record<number, number> = {
    2: 0.4, 2.5: 0.45, 3: 0.5, 4: 0.7, 5: 0.8, 6: 1.0,
    8: 1.25, 10: 1.5, 12: 1.75, 16: 2.0, 20: 2.5,
  };

  /** Real ISO thread (60° profile). Pitch defaults from the ISO coarse table. */
  async thread(opts: {
    diameter: number;
    length: number | { $expr: string };
    pitch?: number;
    axis?: 'X' | 'Y' | 'Z';
    axisOrigin?: Vec3;
    leftHanded?: boolean;
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const pitch = opts.pitch ?? CadApi.ISO_PITCH[opts.diameter];
    if (!pitch) {
      throw new Error(
        `No ISO pitch known for ⌀${opts.diameter} — pass pitch explicitly (table: M${Object.keys(CadApi.ISO_PITCH).join('/M')})`,
      );
    }
    const n = this.doc.timeline.features.filter((f) => f.type === 'thread').length + 1;
    const name = opts.name ?? `M${opts.diameter} Thread ${n}`;
    return this.add(name, (t) =>
      t.addFeature('thread', name, {
        axisOrigin: opts.axisOrigin ?? [0, 0, 0],
        axis: opts.axis ?? 'Z',
        diameter: opts.diameter,
        pitch,
        length: opts.length as never,
        ...(opts.leftHanded ? { leftHanded: true } : {}),
        op: opts.op ?? 'new-body',
      }).timeline,
    );
  }

  /** Helical coil (spring/thread): exact helix sweep of a circular wire. */
  async coil(opts: {
    radius: number | { $expr: string };
    pitch: number | { $expr: string };
    turns: number | { $expr: string };
    wireDiameter: number | { $expr: string };
    axis?: 'X' | 'Y' | 'Z';
    axisOrigin?: Vec3;
    leftHanded?: boolean;
    op?: 'new-body' | 'join' | 'cut' | 'intersect';
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'coil').length + 1;
    const name = opts.name ?? `Coil ${n}`;
    return this.add(name, (t) =>
      t.addFeature('coil', name, {
        axisOrigin: opts.axisOrigin ?? [0, 0, 0],
        axis: opts.axis ?? 'Z',
        radius: opts.radius as never,
        pitch: opts.pitch as never,
        turns: opts.turns as never,
        wireDiameter: opts.wireDiameter as never,
        ...(opts.leftHanded ? { leftHanded: true } : {}),
        op: opts.op ?? 'new-body',
      }).timeline,
    );
  }

  /** Exact volume (mm³), surface area (mm²), center of mass of a body. */
  /** Grams for a body: per-component densities for parts, else volume × ρ. */
  private bodyMassGrams(id: FeatureId, volume: number, mat: MaterialDef): number {
    const components = this.doc.componentMaterialsOf(id);
    if (components) {
      let g = 0;
      for (const c of components) g += massOf(c.volume, materialById(c.material) ?? mat);
      return g;
    }
    return massOf(volume, mat);
  }

  async massProps(
    bodyId: string,
  ): Promise<{ volume: number; area: number; centerOfMass: Vec3; material?: string; massGrams?: number }> {
    const props = await this.doc.massProps(bodyId as FeatureId);
    const mat = this.doc.resolveMaterial(bodyId as FeatureId)?.material;
    if (!mat) return props;
    return {
      ...props,
      material: mat.name,
      massGrams: Math.round(this.bodyMassGrams(bodyId as FeatureId, props.volume, mat) * 100) / 100,
    };
  }

  /** The material library: appearance + FEA-grade mechanical properties. */
  listMaterials(): {
    id: string; name: string; category: string; color: string;
    density: number; youngsModulus: number; poissonRatio: number;
    yieldStrength: number; ultimateStrength: number; thermalExpansion: number;
  }[] {
    return MATERIALS.map((m) => ({
      id: m.id, name: m.name, category: m.category, color: m.appearance.color,
      ...m.physical,
    }));
  }

  /**
   * Assign a body's material and/or color override. PATCH semantics: an
   * omitted field keeps the node's current value; an explicit null clears it
   * — so set_material({bodyId, color}) tints without dropping the material.
   */
  async setMaterial(opts: {
    bodyId: string;
    material?: string | null;
    color?: string | null;
  }): Promise<{ id: string; material: string | null; color?: string }> {
    const id = opts.bodyId as FeatureId;
    const node = this.doc.timeline.get(id);
    if (!node) throw new Error(`No such feature ${opts.bodyId}`);
    const matId =
      opts.material === undefined ? node.material : opts.material === null ? undefined : opts.material;
    if (matId !== undefined && !materialById(matId)) {
      throw new Error(
        `Unknown material "${matId}" — list_materials shows the library (e.g. pla, aluminum-6061, steel-mild)`,
      );
    }
    const color =
      opts.color === undefined ? node.materialColor : opts.color === null ? undefined : opts.color;
    if (color !== undefined && !/^#[0-9a-f]{6}$/i.test(color)) {
      throw new Error('color must be #rrggbb');
    }
    await this.doc.apply(`Material: ${matId ?? 'none'}`, (t) => t.setMaterial(id, matId, color));
    return { id: opts.bodyId, material: matId ?? null, ...(color ? { color } : {}) };
  }

  /**
   * A body's effective material (own or inherited through consumed
   * ancestors) with the full mechanical property set — the FEA contract.
   */
  materialInfo(bodyId: string): {
    material: string; name: string; color: string; colorOverride?: string;
    density: number; youngsModulus: number; poissonRatio: number;
    yieldStrength: number; ultimateStrength: number; thermalExpansion: number;
    massGrams?: number;
    /** Inserted parts with more than one source material list them here. */
    components?: { material: string | null; volume: number; massGrams: number | null }[];
    note?: string;
  } | null {
    const id = bodyId as FeatureId;
    const resolved = this.doc.resolveMaterial(id);
    if (!resolved?.material) return null;
    const m = resolved.material;
    const volume = this.bodyInfo(bodyId)?.volume;
    const components = this.doc.componentMaterialsOf(id);
    const distinct = components
      ? new Set(components.map((c) => c.material ?? 'none')).size
      : 0;
    return {
      material: m.id, name: m.name, color: m.appearance.color,
      ...(resolved.color ? { colorOverride: resolved.color } : {}),
      ...m.physical,
      ...(volume !== undefined
        ? { massGrams: Math.round(this.bodyMassGrams(id, volume, m) * 100) / 100 }
        : {}),
      ...(components && distinct > 1
        ? {
            components: components.map((c) => {
              const cm = materialById(c.material);
              return {
                material: c.material ?? null,
                volume: Math.round(c.volume * 100) / 100,
                massGrams: cm ? Math.round(massOf(c.volume, cm) * 100) / 100 : null,
              };
            }),
            note: 'mixed-material part — mechanical properties above are the DOMINANT component\'s; mass sums all components. FEA on mixed parts uses the dominant material.',
          }
        : {}),
    };
  }

  /**
   * Exact minimum distance between two bodies with the closest points on
   * each (0 = touching/overlapping — use interference to tell which).
   */
  async distance(
    bodyIdA: string,
    bodyIdB: string,
  ): Promise<{ distance: number; pointA: Vec3; pointB: Vec3 }> {
    return this.doc.distance(bodyIdA as FeatureId, bodyIdB as FeatureId);
  }

  /**
   * One-call situation report: every VISIBLE body (not consumed) with bbox,
   * volume, brep availability — plus the parameter table. Saves the
   * list_features → body_info × N round-trips.
   */
  sceneInfo(): {
    bodies: {
      id: string; name: string; type: string; bbox: { min: Vec3; max: Vec3 };
      volume: number; hasBrep: boolean; material?: string; massGrams?: number;
    }[];
    parameters: Record<string, number>;
    featureCount: number;
    errors: { id: string; name: string; message: string }[];
  } {
    const report = this.doc.lastReport;
    const bodies = [];
    const errors = [];
    for (const f of this.doc.timeline.features) {
      const status = report?.statuses.get(f.id);
      if (status?.state === 'error') {
        errors.push({ id: f.id, name: f.name, message: status.message });
      }
      const result = report?.results.get(f.id);
      if (result?.kind !== 'body' || report!.consumed.has(f.id)) continue;
      const info = this.bodyInfo(f.id);
      if (!info) continue;
      const mat = this.doc.resolveMaterial(f.id)?.material;
      bodies.push({
        id: f.id,
        name: f.name,
        type: f.type,
        bbox: info.bbox,
        volume: info.volume,
        hasBrep: !!result.brepId,
        ...(mat
          ? {
              material: mat.id,
              massGrams: Math.round(this.bodyMassGrams(f.id, info.volume, mat) * 100) / 100,
            }
          : {}),
      });
    }
    let parameters: Record<string, number> = {};
    try {
      parameters = evalParamTable(this.doc.timeline.params).values;
    } catch {
      /* a broken expression shouldn't kill the report */
    }
    return { bodies, parameters, featureCount: this.doc.timeline.features.length, errors };
  }

  /**
   * "See" a body's silhouette from a standard (or custom) view without a
   * screenshot: occlusion-correct visible + hidden edges and circle centres,
   * as 2D segments in the view plane, plus a bbox. Great for confirming
   * overall shape/proportions from a named direction.
   */
  async silhouetteOutline(opts: {
    view: 'iso' | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom' | { dir: Vec3; xdir: Vec3 };
    bodyIds?: string[];
  }): Promise<{
    visibleCount: number;
    hiddenCount: number;
    bbox: { min: [number, number]; max: [number, number] } | null;
    visible: [number, number, number, number][];
    hidden: [number, number, number, number][];
    circles: [number, number, number][];
  }> {
    const S3 = 0.5773502691896258;
    const VIEWS: Record<string, { dir: Vec3; xdir: Vec3 }> = {
      front: { dir: [0, -1, 0], xdir: [1, 0, 0] },
      back: { dir: [0, 1, 0], xdir: [-1, 0, 0] },
      right: { dir: [1, 0, 0], xdir: [0, 1, 0] },
      left: { dir: [-1, 0, 0], xdir: [0, -1, 0] },
      top: { dir: [0, 0, 1], xdir: [1, 0, 0] },
      bottom: { dir: [0, 0, -1], xdir: [1, 0, 0] },
      iso: { dir: [S3, -S3, S3], xdir: [0.7071, 0.7071, 0] },
    };
    const v = typeof opts.view === 'string' ? VIEWS[opts.view] : opts.view;
    if (!v) throw new Error(`Unknown view "${opts.view}" (iso|front|back|left|right|top|bottom or {dir,xdir})`);

    // Resolve requested bodies (default: all visible exact solids).
    const report = this.doc.lastReport;
    const brepIds: string[] = [];
    if (opts.bodyIds) {
      for (const id of opts.bodyIds) {
        const b = this.doc.brepIdOf(id as FeatureId);
        if (!b) throw new Error(`${id} has no exact B-rep body`);
        brepIds.push(b);
      }
    } else if (report) {
      for (const [id, r] of report.results) {
        if (r.kind === 'body' && r.brepId && !report.consumed.has(id) && r.mesh.positions.length) {
          brepIds.push(r.brepId);
        }
      }
    }
    if (brepIds.length === 0) throw new Error('No exact B-rep bodies to project');

    const geo = await this.doc.projectViewOf(
      brepIds,
      [v.dir[0], v.dir[1], v.dir[2]],
      [v.xdir[0], v.xdir[1], v.xdir[2]],
    );
    const min: [number, number] = [Infinity, Infinity];
    const max: [number, number] = [-Infinity, -Infinity];
    const r = (x: number): number => Math.round(x * 1000) / 1000;
    const reshape = (flat: Float32Array, cap: number): [number, number, number, number][] => {
      const out: [number, number, number, number][] = [];
      for (let i = 0; i + 3 < flat.length; i += 4) {
        for (const [x, y] of [[flat[i]!, flat[i + 1]!], [flat[i + 2]!, flat[i + 3]!]] as const) {
          min[0] = Math.min(min[0], x); min[1] = Math.min(min[1], y);
          max[0] = Math.max(max[0], x); max[1] = Math.max(max[1], y);
        }
        if (out.length < cap) out.push([r(flat[i]!), r(flat[i + 1]!), r(flat[i + 2]!), r(flat[i + 3]!)]);
      }
      return out;
    };
    const visible = reshape(geo.visible, 300);
    const hidden = reshape(geo.hidden, 150);
    const circles: [number, number, number][] = [];
    for (let i = 0; i + 2 < geo.circles.length; i += 3) {
      circles.push([r(geo.circles[i]!), r(geo.circles[i + 1]!), r(geo.circles[i + 2]!)]);
    }
    return {
      visibleCount: Math.floor(geo.visible.length / 4),
      hiddenCount: Math.floor(geo.hidden.length / 4),
      bbox: min[0] === Infinity ? null : { min: [r(min[0]), r(min[1])], max: [r(max[0]), r(max[1])] },
      visible,
      hidden,
      circles,
    };
  }

  /**
   * Measure the feature nearest a world point: read back a hole's actual
   * ⌀ + depth, an achieved fillet radius, an edge length. want 'face'
   * (surface metrics), 'edge' (curve metrics), or 'auto' (both nearest).
   */
  async measureFeature(
    bodyId: string,
    near: Vec3,
    want: 'auto' | 'face' | 'edge' = 'auto',
  ): Promise<import('@core/geometry/types').MeasureResult> {
    return this.doc.measureNear(bodyId as FeatureId, [near[0], near[1], near[2]], want);
  }

  /** Topology census: solids/shells/faces/edges by kind. */
  async topologySummary(bodyId: string): Promise<import('@core/geometry/types').TopologySummary> {
    return this.doc.topologySummary(bodyId as FeatureId);
  }

  /** Sampled wall-thickness distribution (DFM: did the shell come out right?). */
  async wallThickness(
    bodyId: string,
    samples = 200,
  ): Promise<import('@core/geometry/types').WallThicknessResult> {
    return this.doc.wallThickness(bodyId as FeatureId, Math.min(Math.max(samples, 20), 2000));
  }

  /**
   * Whole-assembly validation in one call: every visible body pair is
   * AABB-prefiltered, overlapping candidates get an exact interference
   * check, near pairs an exact clearance. O(n²) pairs but exact ops run
   * only where bboxes say they might matter.
   */
  async assemblyClash(opts?: { clearance?: number }): Promise<{
    bodies: number;
    skippedNoBrep: number;
    interferences: { a: string; b: string; aName: string; bName: string; overlapVolume: number }[];
    touching: { a: string; b: string; aName: string; bName: string }[];
    tooClose: { a: string; b: string; aName: string; bName: string; distance: number }[];
  }> {
    const clearance = Math.max(0, opts?.clearance ?? 0.5);
    const scene = this.sceneInfo();
    const bodies = scene.bodies.filter((b) => b.hasBrep);
    const interferences: { a: string; b: string; aName: string; bName: string; overlapVolume: number }[] = [];
    const touching: { a: string; b: string; aName: string; bName: string }[] = [];
    const tooClose: { a: string; b: string; aName: string; bName: string; distance: number }[] = [];
    // Bboxes come from the DISPLAY MESH, whose vertices can sit up to the
    // tessellation deflection inside a curved face's true extreme — pad each
    // gate by both bodies' deflections so the prefilter stays conservative
    // (same formula as occtCore.tessellate).
    const slack = (b: (typeof bodies)[0]): number => {
      const d = Math.hypot(
        b.bbox.max[0] - b.bbox.min[0], b.bbox.max[1] - b.bbox.min[1], b.bbox.max[2] - b.bbox.min[2],
      );
      return Math.min(Math.max(d * 0.004, 0.05), 1);
    };
    const overlaps = (
      a: (typeof bodies)[0], b: (typeof bodies)[0], pad: number,
    ): boolean =>
      a.bbox.min[0] - pad <= b.bbox.max[0] && a.bbox.max[0] + pad >= b.bbox.min[0] &&
      a.bbox.min[1] - pad <= b.bbox.max[1] && a.bbox.max[1] + pad >= b.bbox.min[1] &&
      a.bbox.min[2] - pad <= b.bbox.max[2] && a.bbox.max[2] + pad >= b.bbox.min[2];
    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const A = bodies[i]!, B = bodies[j]!;
        const pad = slack(A) + slack(B);
        if (!overlaps(A, B, clearance + pad)) continue; // bboxes clear → pair clear
        if (overlaps(A, B, pad)) {
          const v = (await this.interference(A.id, B.id)).overlapVolume;
          if (v > 1e-6) {
            interferences.push({ a: A.id, b: B.id, aName: A.name, bName: B.name, overlapVolume: Math.round(v * 1000) / 1000 });
            continue;
          }
        }
        const d = (await this.distance(A.id, B.id)).distance;
        // Contact (mates stack/flush parts) is CLEAR, matching interference's
        // "touching faces count as clear" — report it separately, not as a gap.
        if (d <= 1e-6) {
          touching.push({ a: A.id, b: B.id, aName: A.name, bName: B.name });
        } else if (d < clearance) {
          tooClose.push({ a: A.id, b: B.id, aName: A.name, bName: B.name, distance: Math.round(d * 1000) / 1000 });
        }
      }
    }
    return { bodies: bodies.length, skippedNoBrep: scene.bodies.length - bodies.length, interferences, touching, tooClose };
  }

  /**
   * Structural FEA (voxel hex, linear elasticity) on one body: fixture faces
   * held, loads applied, von Mises stress painted on the body + summary
   * numbers returned. Material comes from the body (set_material) or an
   * explicit override. Units: N in → MPa / mm out.
   */
  async runFea(opts: {
    bodyId: string;
    fixtures: { facePoint: Vec3 }[];
    loads: { facePoint: Vec3; force?: Vec3; magnitude?: number; pressure?: number }[];
    resolution?: number;
    material?: string;
    /** Deformed-view exaggeration; omit/0 = auto, 1 = true scale. */
    deformationScale?: number;
  }): Promise<{
    converged: boolean;
    material: string;
    elements: number;
    nodes: number;
    iterations: number;
    voxelSizeMm: number;
    maxVonMisesMPa: number;
    hotspot: Vec3;
    maxDisplacementMm: number;
    maxDisplacementAt: Vec3;
    factorOfSafety: number;
    yieldStrengthMPa: number;
    note?: string;
  }> {
    const id = opts.bodyId as FeatureId;
    const result = this.doc.lastReport?.results.get(id);
    if (result?.kind !== 'body' || result.mesh.positions.length === 0) {
      throw new Error(`Feature ${opts.bodyId} has no body geometry`);
    }
    if (this.doc.lastReport?.consumed.has(id)) {
      throw new Error(
        'Body was consumed by a later feature — run FEA on the newest feature in its chain (scene_info shows visible bodies)',
      );
    }
    const mesh = result.mesh;
    if (!mesh.triFaceIndex || !mesh.faceData) {
      throw new Error('FEA face selection needs an exact B-rep body');
    }
    const isPoint = (p: unknown): p is [number, number, number] =>
      Array.isArray(p) && p.length === 3 && p.every((v) => Number.isFinite(v));
    for (const [what, list] of [['fixture', opts.fixtures], ['load', opts.loads]] as const) {
      if (!Array.isArray(list) || list.length === 0) throw new Error(`FEA needs at least one ${what}`);
      for (const item of list) {
        if (!isPoint(item?.facePoint)) {
          throw new Error(`Each ${what} needs facePoint:[x,y,z] (use face_info centroids)`);
        }
      }
    }
    const mat = opts.material
      ? materialById(opts.material)
      : this.doc.resolveMaterial(id)?.material;
    if (!mat) {
      throw new Error(
        'FEA needs a material for E/ν/yield — set_material on the body first, or pass material',
      );
    }
    // magnitude loads press along −(face outward normal); negative pulls.
    // The face is resolved EXACTLY like the solver resolves BC faces
    // (nearest triangle by true distance), so direction and distribution
    // always come from the same face.
    const faceNormalNear = (p: [number, number, number]): Vec3 => {
      const tri = nearestFaceTriangle(mesh.positions, mesh.indices, p);
      const f = tri >= 0 ? mesh.triFaceIndex![tri]! : -1;
      // Only PLANAR faces carry an outward normal in faceData — cylinders
      // pack their AXIS in the same slots, which would silently misdirect
      // the whole load; freeform faces carry zeros.
      if (f < 0 || mesh.faceKinds?.[f] !== 1) {
        throw new Error(
          'magnitude/pressure loads need a PLANAR face (this one is cylindrical/curved) — pass an explicit force:[x,y,z] instead',
        );
      }
      const fd = mesh.faceData!;
      return [fd[f * 10 + 3]!, fd[f * 10 + 4]!, fd[f * 10 + 5]!];
    };
    // Exact area of the B-rep face under a point (sums its display triangles).
    const faceAreaNear = (p: [number, number, number]): number => {
      const tri = nearestFaceTriangle(mesh.positions, mesh.indices, p);
      if (tri < 0) return 0;
      const face = mesh.triFaceIndex![tri]!;
      const P = mesh.positions, I = mesh.indices;
      let area = 0;
      for (let t = 0; t * 3 < I.length; t++) {
        if (mesh.triFaceIndex![t] !== face) continue;
        const a = I[t * 3]! * 3, b = I[t * 3 + 1]! * 3, c = I[t * 3 + 2]! * 3;
        const ux = P[b]! - P[a]!, uy = P[b + 1]! - P[a + 1]!, uz = P[b + 2]! - P[a + 2]!;
        const vx = P[c]! - P[a]!, vy = P[c + 1]! - P[a + 1]!, vz = P[c + 2]! - P[a + 2]!;
        area += 0.5 * Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx);
      }
      return area;
    };
    const loads = opts.loads.map((l) => {
      const given = [l.force, l.magnitude, l.pressure].filter((v) => v !== undefined).length;
      if (given > 1) {
        throw new Error('Each load takes exactly ONE of force, magnitude, or pressure');
      }
      if (l.force) {
        if (!isPoint(l.force)) throw new Error('load force must be [x,y,z] in N');
        return { point: l.facePoint as [number, number, number], force: l.force as [number, number, number] };
      }
      // pressure (MPa = N/mm²) × exact face area → total force into the face.
      let magnitude = l.magnitude;
      if (magnitude === undefined && l.pressure !== undefined) {
        if (!(Number.isFinite(l.pressure) && l.pressure !== 0)) {
          throw new Error('pressure must be a nonzero number in MPa (negative = suction)');
        }
        const area = faceAreaNear(l.facePoint as [number, number, number]);
        if (!(area > 0)) throw new Error('Could not resolve a face for the pressure load');
        magnitude = l.pressure * area;
      }
      if (!(magnitude !== undefined && Number.isFinite(magnitude) && magnitude !== 0)) {
        throw new Error(
          'Each load needs force:[x,y,z], magnitude (N; + presses into the face, − pulls), or pressure (MPa)',
        );
      }
      const n = faceNormalNear(l.facePoint as [number, number, number]);
      return {
        point: l.facePoint as [number, number, number],
        force: [-n[0] * magnitude, -n[1] * magnitude, -n[2] * magnitude] as [number, number, number],
      };
    });
    const r = await runFeaSolve({
      positions: mesh.positions,
      indices: mesh.indices,
      triFaceIndex: mesh.triFaceIndex,
      E: mat.physical.youngsModulus * 1000, // GPa → MPa
      nu: mat.physical.poissonRatio,
      yieldStrength: mat.physical.yieldStrength,
      fixtures: opts.fixtures.map((f) => ({ point: f.facePoint as [number, number, number] })),
      loads,
      resolution: opts.resolution,
    });
    // The model may have been edited while the worker solved — only paint if
    // this body's geometry is still the exact result we analyzed.
    const fresh = this.doc.lastReport?.results.get(id) === result;
    if (r.converged && fresh) {
      this.ui.showStress?.(id, r.vertexVonMises, {
        maxVonMises: r.maxVonMises,
        factorOfSafety: r.factorOfSafety,
        material: mat.name,
        displacement: r.vertexDisplacement,
        maxDisplacement: r.maxDisplacement,
        deformationScale: opts.deformationScale ?? 0,
      });
    }
    const notes: string[] = [];
    if (!r.converged) {
      notes.push('did NOT converge — check the body is one connected solid and fixtures actually hold it');
    }
    if (!fresh) notes.push('the model changed during the solve — results reflect the pre-edit geometry; re-run');
    if (r.unmappedVertices > 0) {
      notes.push(`${r.unmappedVertices} display vertices sit on features thinner than a voxel and show 0 stress — raise resolution to resolve them`);
    }
    const rv = (x: number): number => Math.round(x * 1000) / 1000;
    return {
      converged: r.converged,
      material: mat.name,
      elements: r.elements,
      nodes: r.nodes,
      iterations: r.iterations,
      voxelSizeMm: rv(r.voxelSize),
      maxVonMisesMPa: rv(r.maxVonMises),
      hotspot: r.hotspot.map(rv) as unknown as Vec3,
      maxDisplacementMm: Number(r.maxDisplacement.toPrecision(4)),
      maxDisplacementAt: r.maxDisplacementAt.map(rv) as unknown as Vec3,
      factorOfSafety: rv(r.factorOfSafety),
      yieldStrengthMPa: mat.physical.yieldStrength,
      ...(notes.length ? { note: notes.join('; ') } : {}),
    };
  }

  /** Remove the FEA stress overlay from the viewport. */
  clearFea(): { ok: true } {
    this.ui.clearStress?.();
    return { ok: true };
  }

  /** Exact overlap volume (mm³) between two bodies; 0 = no interference. */
  async interference(bodyIdA: string, bodyIdB: string): Promise<{ overlapVolume: number; interferes: boolean }> {
    const v = await this.doc.interference(bodyIdA as FeatureId, bodyIdB as FeatureId);
    return { overlapVolume: v, interferes: v > 1e-6 };
  }

  /**
   * "See" a body without vision: the exact plane∩body cut outline at
   * axis=offset, as 2D segments in the section plane (X→(Y,Z), Y→(X,Z),
   * Z→(X,Y)), plus its bounding box and a closed-loop count. Great for
   * confirming an internal shape (a bore, a rib) turned out as intended.
   */
  async crossSection(
    bodyId: string,
    axis: 'X' | 'Y' | 'Z',
    offset: number,
  ): Promise<{
    segmentCount: number;
    closedLoops: number;
    bbox: { min: [number, number]; max: [number, number] } | null;
    segments: [number, number, number, number][];
  }> {
    const brepId = this.doc.brepIdOf(bodyId as FeatureId);
    if (!brepId || !this.doc.crossSectionOf) {
      throw new Error('Cross-section needs an exact B-rep body');
    }
    const flat = await this.doc.crossSectionOf(brepId, axis, offset);
    const n = Math.floor(flat.length / 4);
    if (n === 0) return { segmentCount: 0, closedLoops: 0, bbox: null, segments: [] };
    const min: [number, number] = [Infinity, Infinity];
    const max: [number, number] = [-Infinity, -Infinity];
    const all: [number, number, number, number][] = [];
    const segments: [number, number, number, number][] = [];
    const MAX = 200;
    const r = (v: number): number => Math.round(v * 1000) / 1000;
    for (let i = 0; i < n; i++) {
      const seg: [number, number, number, number] = [
        flat[i * 4]!, flat[i * 4 + 1]!, flat[i * 4 + 2]!, flat[i * 4 + 3]!,
      ];
      all.push(seg);
      for (const [x, y] of [[seg[0], seg[1]], [seg[2], seg[3]]] as const) {
        min[0] = Math.min(min[0], x); min[1] = Math.min(min[1], y);
        max[0] = Math.max(max[0], x); max[1] = Math.max(max[1], y);
      }
      if (segments.length < MAX) segments.push([r(seg[0]), r(seg[1]), r(seg[2]), r(seg[3])]);
    }
    return {
      segmentCount: n,
      closedLoops: countClosedLoops(all), // e.g. a tube section = 2 (outer+inner)
      bbox: { min: [r(min[0]), r(min[1])], max: [r(max[0]), r(max[1])] },
      segments,
    };
  }

  /**
   * Mate: anchor (bbox translation) or concentric (align cylindrical faces
   * coaxially — pass a point near each cylinder to identify the faces).
   */
  async mate(opts: {
    moveBodyId: string;
    ontoBodyId: string;
    kind?: 'anchor' | 'concentric' | 'flush' | 'parallel' | 'distance' | 'angle';
    moveAnchor?: 'center' | 'bottom' | 'top' | 'min' | 'max';
    ontoAnchor?: 'center' | 'bottom' | 'top' | 'min' | 'max';
    offset?: Vec3;
    moveFacePoint?: Vec3;
    ontoFacePoint?: Vec3;
    gap?: number | { $expr: string };
    angleDeg?: number | { $expr: string };
    name?: string;
  }): Promise<{ id: string; name: string; status: string }> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'mate').length + 1;
    const name = opts.name ?? `Mate ${n}`;
    const faceKinds = ['concentric', 'flush', 'parallel', 'distance', 'angle'];
    if (opts.kind && faceKinds.includes(opts.kind) && (!opts.moveFacePoint || !opts.ontoFacePoint)) {
      throw new Error(`${opts.kind} mate needs moveFacePoint and ontoFacePoint near the two faces (see face_info)`);
    }
    const facePart =
      opts.kind && faceKinds.includes(opts.kind)
        ? {
            kind: opts.kind,
            faceTarget: opts.moveFacePoint,
            faceTo: opts.ontoFacePoint,
            ...(opts.gap !== undefined ? { gap: opts.gap } : {}),
            ...(opts.angleDeg !== undefined ? { angleDeg: opts.angleDeg } : {}),
          }
        : {};
    return this.add(name, (t) =>
      t.addFeature('mate', name, {
        target: ref(opts.moveBodyId as FeatureId),
        to: ref(opts.ontoBodyId as FeatureId),
        ...facePart,
        anchorTarget: opts.moveAnchor ?? 'bottom',
        anchorTo: opts.ontoAnchor ?? 'top',
        offset: opts.offset ?? [0, 0, 0],
      } as never).timeline,
    );
  }

  /** Workspace projects (assembly sources). */
  async listProjects(): Promise<{ id: string; name: string; features: number; current: boolean }[]> {
    const { db } = await import('@core/storage/db');
    const all = await db.projects.orderBy('updatedAt').reverse().toArray();
    return all.map((p) => ({
      id: p.id,
      name: p.name,
      features: p.timeline.features.length,
      current: p.id === this.doc.id,
    }));
  }

  // --- edits ----------------------------------------------------------------------

  async updateFeature(id: string, patch: Record<string, unknown>): Promise<{ status: string }> {
    const feature = this.doc.timeline.get(id as FeatureId);
    if (!feature) throw new Error(`No feature ${id}`);
    const params = { ...(feature.params as unknown as Record<string, unknown>), ...patch };
    await this.doc.apply(`Edit ${feature.name}`, (t) =>
      t.updateFeature(id as FeatureId, params as never),
    );
    const s = this.featureStatus(id);
    if (s.state === 'error') throw new Error(`Edit applied but ${feature.name} failed: ${s.message}`);
    return { status: s.state };
  }

  async renameFeature(id: string, name: string): Promise<void> {
    await this.doc.apply(`Rename to ${name}`, (t) => t.renameFeature(id as FeatureId, name));
  }

  async deleteFeature(id: string): Promise<{ deleted: string[] }> {
    const t = this.doc.timeline;
    const doomed = t.downstreamOf(id as FeatureId).map((d) => t.get(d)?.name ?? d);
    await this.doc.apply(`Delete ${t.get(id as FeatureId)?.name ?? id}`, (tl) =>
      tl.removeFeature(id as FeatureId, { cascade: true }),
    );
    return { deleted: doomed };
  }

  async suppressFeature(id: string, suppressed: boolean): Promise<void> {
    await this.doc.apply(`${suppressed ? 'Suppress' : 'Unsuppress'} feature`, (t) =>
      t.setSuppressed(id as FeatureId, suppressed),
    );
  }

  async undo(): Promise<void> {
    await this.doc.undo();
  }

  async redo(): Promise<void> {
    await this.doc.redo();
  }

  /**
   * A checkpoint token capturing the current project + history depth. Grab
   * one before a risky multi-step build; rollback(token) later restores this
   * exact state (undoing everything since). The token is bound to THIS
   * project — rollback rejects it if you've since switched projects.
   */
  checkpoint(): { checkpoint: string; features: number } {
    return {
      checkpoint: `${this.doc.id}#${this.doc.historyDepth}`,
      features: this.doc.timeline.features.length,
    };
  }

  /** Undo back to a checkpoint token from checkpoint(). Returns steps undone. */
  async rollback(checkpoint: string): Promise<{ rolledBack: number; features: number }> {
    if (typeof checkpoint !== 'string' || !checkpoint.includes('#')) {
      throw new Error('rollback needs the checkpoint token string from checkpoint()');
    }
    const [docId, depthStr] = checkpoint.split('#');
    if (docId !== this.doc.id) {
      throw new Error(
        'Checkpoint belongs to a different project — switch back to it before rolling back',
      );
    }
    const depth = Number(depthStr);
    if (!Number.isInteger(depth) || depth < 0) throw new Error('Malformed checkpoint token');
    const rolledBack = await this.doc.rollbackTo(depth);
    return { rolledBack, features: this.doc.timeline.features.length };
  }

  /** Make rolled-back states unreachable via Redo (dry_run cleanup). */
  discardRedo(): void {
    this.doc.discardRedo();
  }

  // --- projects (part libraries, assemblies) ----------------------------------

  /** Create a new empty project and switch to it (for parts / assemblies). */
  async createProject(name?: string): Promise<{ id: string; name: string }> {
    if (!this.ui.createProject) throw new Error('Project management unavailable in this context');
    const id = await this.ui.createProject(name);
    return { id, name: this.doc.name };
  }

  /** Switch the live document to an existing project by id. */
  async switchProject(id: string): Promise<{ id: string; name: string }> {
    if (!this.ui.switchProject) throw new Error('Project management unavailable in this context');
    await this.ui.switchProject(id);
    return { id: this.doc.id, name: this.doc.name };
  }

  /**
   * Permanently delete a project by id (cannot be undone). If it is the live
   * project, the app switches to another survivor (or a fresh blank one if it
   * was the last). Returns the now-current project.
   */
  async deleteProject(id: string): Promise<{ deleted: string; current: { id: string; name: string } }> {
    if (!this.ui.deleteProject) throw new Error('Project management unavailable in this context');
    const projects = await this.listProjects();
    if (!projects.some((p) => p.id === id)) throw new Error(`No project ${id}`);
    await this.ui.deleteProject(id);
    return { deleted: id, current: { id: this.doc.id, name: this.doc.name } };
  }

  // --- view -----------------------------------------------------------------------

  /** Select a feature in the GUI — points the human at what you mean. */
  selectFeature(id: string | null): { selected: string | null } {
    if (id !== null && !this.doc.timeline.get(id as FeatureId)) throw new Error(`No feature ${id}`);
    this.ui.select?.(id as FeatureId | null);
    return { selected: id };
  }

  /** Viewport screenshot as a data URL, or null when headless. */
  async captureView(view?: string): Promise<string | null> {
    return this.ui.capture ? this.ui.capture(view) : null;
  }

  fitView(): void {
    this.ui.fitView?.();
  }

  /** Escape hatch used by run_js / custom tools for anything not wrapped. */
  raw(): { doc: CadDocument } {
    return { doc: this.doc };
  }
}

export type { GeometryResult };
